// Copyright (c) Ping. All rights reserved.
//
// Outbound hydration envelopes for MLS groups (create + add-member).

import type { MlsConversationLike } from './mlsConversation';

export interface SendGroupHydrationParams {
  readonly conv: MlsConversationLike;
  readonly inviterAccountId: string;
  readonly inviterDisplayName: string;
  readonly title?: string | undefined;
  /** Sorted unique peer account ids (excluding the sender / local user). */
  readonly memberAccountIds?: readonly string[] | undefined;
  readonly memberDisplayNames?: Readonly<Record<string, string>> | undefined;
  /** Group avatar (data/remote URL) chosen at create time — broadcast so every
   *  member's chat-list + header avatar matches. */
  readonly avatarUri?: string | undefined;
  /** Stable group-avatar media id. Rides the `com.ping.group_name` payload so a
   *  web JOINER (who can't read the GroupContext meta on their Welcome) still
   *  gets the group picture — `groupNamePayloadToConversationPatch` reads it and
   *  the host resolves it to a URL. This is the ONLY group-avatar channel that
   *  reliably reaches a fresh web joiner on the add-member path. */
  readonly avatarMediaId?: string | undefined;
  readonly log?: { warn: (meta: Record<string, unknown>) => void } | undefined;
}

/** Best-effort MLS application messages that hydrate receiver rows. */
export async function sendGroupHydrationMessages(
  params: SendGroupHydrationParams,
): Promise<void> {
  const {
    conv,
    inviterAccountId,
    inviterDisplayName,
    title,
    memberAccountIds,
    memberDisplayNames,
    avatarUri,
    avatarMediaId,
    log,
  } = params;

  const trimmedAvatarMediaId =
    avatarMediaId !== undefined && avatarMediaId.length > 0 ? avatarMediaId : undefined;
  const trimmedTitle = title?.trim();
  if (trimmedTitle !== undefined && trimmedTitle.length > 0) {
    try {
      const groupName: Record<string, unknown> = {
        kind: 'com.ping.group_name',
        payload: {
          name: trimmedTitle,
          ...(trimmedAvatarMediaId !== undefined
            ? { avatarMediaId: trimmedAvatarMediaId }
            : {}),
          ...(memberAccountIds !== undefined && memberAccountIds.length > 0
            ? { memberAccountIds: [...memberAccountIds] }
            : {}),
          ...(memberDisplayNames !== undefined &&
          Object.keys(memberDisplayNames).length > 0
            ? { memberDisplayNames: { ...memberDisplayNames } }
            : {}),
        },
      };
      await conv.send(new TextEncoder().encode(JSON.stringify(groupName)));
    } catch (err) {
      log?.warn({
        event: 'groupHydration.groupName.failed',
        error: (err as Error).message,
      });
    }
  }

  if ((avatarUri !== undefined && avatarUri.length > 0) || trimmedAvatarMediaId !== undefined) {
    try {
      const groupAvatar: Record<string, unknown> = {
        kind: 'com.ping.group_avatar',
        payload: {
          ...(avatarUri !== undefined && avatarUri.length > 0
            ? { blob_ref: { url: avatarUri } }
            : {}),
          ...(trimmedAvatarMediaId !== undefined
            ? { avatarMediaId: trimmedAvatarMediaId }
            : {}),
        },
      };
      await conv.send(new TextEncoder().encode(JSON.stringify(groupAvatar)));
    } catch (err) {
      log?.warn({
        event: 'groupHydration.groupAvatar.failed',
        error: (err as Error).message,
      });
    }
  }

  try {
    const profile = {
      kind: 'ping.profile',
      payload: {
        accountId: inviterAccountId,
        displayName: inviterDisplayName,
      },
    };
    await conv.send(new TextEncoder().encode(JSON.stringify(profile)));
  } catch (err) {
    log?.warn({
      event: 'groupHydration.profileIntro.failed',
      error: (err as Error).message,
    });
  }
}
